import type { EntityFormArtifactInput } from '@cli-types/next-adapter/forms.js'
import { createGeneratedFile } from '@cli-utils/files.js'
import { usesIdentifier } from '@cli-utils/text.js'
import { renderTabsTrigger } from '@next-adapter/generators/form/form-shared/render-tabs-trigger.js'
import { renderEntityFormContentRenderer } from '@next-adapter/templates/renderers/generators/form/form-entity/generate-form-content.js'
import { renderEntityFormPageRenderer } from '@next-adapter/templates/renderers/generators/form/form-entity/generate-form-page.js'
import { filterNamedImportsByUsage } from './filter-named-imports-by-usage.js'

export function buildEntityFormArtifacts({
  schema,
  pagesDir,
  hookPath,
  hookImportPath,
  actionImportPath,
  plural,
  Singular,
  hookName,
  contentComponentName,
  hookContent,
  uiImports,
  lucideIcons,
  hasRelationship,
  hasSelectCombobox,
  shouldGuardSidebarByTab,
  showMetadataWidget,
  hasTabsField,
  hasDraft,
  topLevelTabsField,
  topLevelTabComponents,
  topLevelTabIconNames,
  hookReturnNames,
  contentStateDestructure,
  sidebarVisibilityState,
  formBodyJSX
}: EntityFormArtifactInput) {
  const needsCn = hasRelationship || hasSelectCombobox || shouldGuardSidebarByTab
  const metadataOffsetConst = showMetadataWidget ? 'const SCROLL_BORDER_OFFSET = 8;\n\n' : ''
  const metadataScrollState = showMetadataWidget
    ? '  const hasScrolled = usePageScrollThreshold(SCROLL_BORDER_OFFSET)\n'
    : ''
  const metadataStrip = showMetadataWidget
    ? renderEntityFormPageRenderer({
        kind: 'renderFormLayoutDetailsBranchBodyTemplate',
        props: {
          plural,
          singular: Singular
        }
      })
    : ''
  const topLevelTabsClassName = showMetadataWidget
    ? renderEntityFormPageRenderer({ kind: 'renderEntityFormPageClassName' })
    : 'className="h-[calc(100svh-56px)] min-h-0 w-full gap-0"'
  const topLevelTabsShell = topLevelTabsField?.tabs
    ? renderEntityFormPageRenderer({
        kind: 'renderFormTabsLayoutTabsTriggerTemplate',
        props: {
          topLevelTabsClassName,
          map: topLevelTabsField.tabs
            .map((tab) => renderTabsTrigger(tab, '            ', { className: 'px-3' }))
            .join('\n'),
          mapTab: topLevelTabsField.tabs
            .map((tab) =>
              renderEntityFormPageRenderer({
                kind: 'renderFormTabContentDetailsTemplate',
                props: {
                  tabName: tab.name,
                  contentComponentName
                }
              })
            )
            .join('\n')
        }
      })
    : ''
  const defaultShellBody = renderEntityFormPageRenderer({
    kind: 'renderEntityFormBody',
    props: { contentComponentName }
  })
  const shellBody = topLevelTabsField ? topLevelTabsShell : defaultShellBody

  const topLevelTabComponentImports = topLevelTabComponents
    .map((spec) => `import { ${spec.componentName} } from './${spec.fileBaseName}'`)
    .join('\n')
  const contentImports = topLevelTabsField
    ? `import { Form } from '@admin/components/ui/form'\nimport type { TabValue, ${hookName} } from '${hookImportPath}'${topLevelTabComponentImports ? `\n${topLevelTabComponentImports}` : ''}`
    : renderEntityFormContentRenderer({
        kind: 'renderFormContentLucideTemplate',
        props: {
          lucideIconsLucide:
            lucideIcons.length > 0
              ? `import { ${lucideIcons.join(', ')} } from 'lucide-react'\n`
              : '',
          hasTabsFieldHookImportPath: hasTabsField
            ? `import type { TabValue, ${hookName} } from '${hookImportPath}'`
            : `import type { ${hookName} } from '${hookImportPath}'`,
          needsCnImport: needsCn ? "\nimport { cn } from '@admin/utils/shared/cn'" : '',
          join: uiImports.join('\n')
        }
      })
  const contentSidebarVisibilityState = topLevelTabsField ? '' : sidebarVisibilityState
  const topLevelTabComponentFiles = topLevelTabComponents.map((spec) => {
    const tabStateNames = hookReturnNames.filter((name) => usesIdentifier(spec.body, name))
    const tabStateDestructure =
      tabStateNames.length > 0
        ? renderEntityFormContentRenderer({
            kind: 'renderFormGeneratedValueTemplate',
            props: {
              map: tabStateNames.map((name) => `    ${name}`).join(',\n')
            }
          })
        : ''
    const tabLucideIcons = lucideIcons.filter((icon) => usesIdentifier(spec.body, icon))
    const tabNeedsCn = /\bcn\s*\(/.test(spec.body)
    const tabImports = [
      tabLucideIcons.length > 0
        ? `import { ${tabLucideIcons.join(', ')} } from 'lucide-react'`
        : '',
      `import type { ${hookName} } from '${hookImportPath}'`,
      tabNeedsCn ? "import { cn } from '@admin/utils/shared/cn'" : '',
      ...filterNamedImportsByUsage(uiImports, spec.body)
    ]
      .filter(Boolean)
      .join('\n')

    return createGeneratedFile(
      `${pagesDir}/${schema.name}/${spec.fileBaseName}.tsx`,
      renderEntityFormContentRenderer({
        kind: 'renderFormClientModuleTemplate',
        props: {
          tabImports,
          specComponentName: spec.componentName,
          hookName,
          tabStateDestructure,
          body: spec.body
        }
      })
    )
  })

  const content = renderEntityFormContentRenderer({
    kind: 'renderEntityFormContent',
    props: {
      contentImports,
      contentComponentName,
      hookName,
      topLevelTabsFieldTab: topLevelTabsField ? '  tab: TabValue\n' : '',
      topLevelTabsFieldTabTab: topLevelTabsField ? ', tab' : '',
      contentStateDestructure,
      contentSidebarVisibilityState,
      schemaName: schema.name,
      hasDraftInitialData: hasDraft ? ', initialData?.published ?? false' : '',
      topLevelTabsFieldClassName: topLevelTabsField
        ? 'className="h-full"'
        : 'className="space-y-6 h-full"',
      formBodyJsx: formBodyJSX
    }
  })

  const shellContent = renderEntityFormPageRenderer({
    kind: 'renderEntityFormPage',
    props: {
      showMetadataWidgetScrollThresholdImport: showMetadataWidget
        ? "\nimport { usePageScrollThreshold } from '@admin/hooks/use-page-scroll-threshold'"
        : '',
      topLevelTabIconNamesLucide:
        topLevelTabIconNames.length > 0
          ? `import { ${topLevelTabIconNames.join(', ')} } from 'lucide-react'\n`
          : '',
      topLevelTabsFieldTabs: topLevelTabsField
        ? "import { Tabs, TabsContent, TabsList, TabsTrigger } from '@admin/components/ui/tabs'\n"
        : '',
      showMetadataWidgetEntityMetadataImport: showMetadataWidget
        ? "\nimport { EntityMetadata } from '@admin/components/shared/entity-metadata'\nimport { cn } from '@admin/utils/shared/cn'"
        : '',
      singular: Singular,
      actionImportPath,
      showMetadataWidgetAdmin: showMetadataWidget
        ? `import { getVersionsBy${Singular}Id, restore${Singular}Version } from '@admin/actions/${actionImportPath}'`
        : '',
      topLevelTabsFieldHookImportPath: topLevelTabsField
        ? `import type { TabValue } from '${hookImportPath}'\n`
        : '',
      hookName,
      hookImportPath,
      contentComponentName,
      schemaName: schema.name,
      metadataOffsetConst,
      metadataScrollState,
      hasDraftFlex: hasDraft
        ? renderEntityFormPageRenderer({ kind: 'renderFormActionButtonsTemplate' })
        : renderEntityFormPageRenderer({ kind: 'renderFormActionButtonsDetailsTemplate' }),
      metadataStrip,
      shellBody
    }
  })

  return {
    files: [
      createGeneratedFile(hookPath, hookContent),
      createGeneratedFile(`${pagesDir}/${schema.name}/${schema.name}-form-content.tsx`, content),
      ...topLevelTabComponentFiles,
      createGeneratedFile(`${pagesDir}/${schema.name}/${schema.name}-form.tsx`, shellContent)
    ]
  }
}
